import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

import { colour, pillSx } from './tokens';
import { formatMoney } from '../../utils/money';

/**
 * Where a part-way family stopped, as two columns you can sort and filter a list of 465 by.
 *
 * The list used to say "Still in progress" about every one of them. That is true of a parent who
 * signed in and closed the tab and of a parent who filled in everything and balked at the amount
 * on the payment page, and those two need opposite conversations — so the one word they shared
 * was the one word that could not help anybody.
 *
 * The server works the stage out in utils/journeyStage.js; nothing is decided here beyond how it
 * reads and what colour it is.
 */

/**
 * The step, as a bar you can take in without reading.
 *
 * Six steps, drawn as six ticks, because a reviewer scanning the column wants "nearly there" or
 * "barely started" before they want the step's name. The name is underneath for when they do.
 */
const StepBar = ({ step, of, label }) => (
    <Box sx={{ minWidth: 0, lineHeight: 1.3, py: 0.5 }}>
        <Box sx={{ display: 'flex', gap: '2px', mb: 0.4 }} aria-hidden>
            {Array.from({ length: of }, (unused, index) => (
                <Box
                    key={index}
                    sx={{
                        width: 12,
                        height: 4,
                        borderRadius: '2px',
                        bgcolor: index < step
                            ? (step === of ? colour.primary : colour.infoInk)
                            : colour.border,
                    }}
                />
            ))}
        </Box>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: colour.body, whiteSpace: 'normal' }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: colour.faint }}>
            {`Step ${step} of ${of}`}
        </Typography>
    </Box>
);

/**
 * How far they got.
 *
 * Sorted by the step number rather than the label, so sorting walks the journey in order instead
 * of alphabetically — "Signed in" first and "Finished" last, not "Answered the discount questions"
 * before "Card tried".
 */
export const journeyColumn = ({ field = 'journey', minWidth = 190 } = {}) => ({
    field,
    headerName: 'Up to',
    minWidth,
    flex: 1,
    valueGetter: (params) => params.row.journey?.step ?? null,
    renderCell: (params) => {
        const journey = params.row.journey;
        if (!journey) return <Typography sx={{ fontSize: '13px', color: colour.faint }}>—</Typography>;

        return <StepBar step={journey.step} of={journey.of} label={journey.short} />;
    },
});

/**
 * What is stopping them, and whose turn it is.
 *
 * "Waiting on us" is the distinction worth the colour. A family in that state must not be sent a
 * reminder — it is our own paperwork they are waiting on, and chasing somebody for our work is
 * how the reminders stop being believed.
 */
export const holdingColumn = ({ field = 'holding', minWidth = 200 } = {}) => ({
    field,
    headerName: 'Holding',
    minWidth,
    flex: 1.2,
    valueGetter: (params) => params.row.journey?.holding ?? null,
    renderCell: (params) => {
        const journey = params.row.journey;

        // A finished family has nothing holding them, and an empty cell says that better than
        // the word "None", which reads like a value somebody still has to check.
        if (!journey || !journey.holding) {
            return <Typography sx={{ fontSize: '13px', color: colour.faint }}>—</Typography>;
        }

        const ours = journey.waitingOn === 'us';

        return (
            <Box sx={{ minWidth: 0, lineHeight: 1.3, py: 0.5 }}>
                <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: colour.body, whiteSpace: 'normal' }}>
                    {journey.holding}
                </Typography>
                <Chip
                    size="small"
                    label={ours ? 'Waiting on us' : 'Waiting on them'}
                    sx={{ ...pillSx(ours ? 'warn' : 'neutral'), mt: 0.4 }}
                />
            </Box>
        );
    },
});

/**
 * What they were being asked for when they stopped — the money columns of the list already say
 * what was *paid*, which for a part-way family is nothing at all.
 *
 * Only for a team that may see amounts: the server leaves `journey.amounts` off the row otherwise,
 * so this renders a dash rather than leaking the figure through a column that should not exist.
 */
export const askedForColumn = ({ field = 'asked_for', minWidth = 150 } = {}) => ({
    field,
    headerName: 'Asked for',
    minWidth,
    flex: 1,
    valueGetter: (params) => params.row.journey?.amounts?.dueNow ?? null,
    renderCell: (params) => {
        const amounts = params.row.journey?.amounts;
        if (!amounts) return <Typography sx={{ fontSize: '13px', color: colour.faint }}>—</Typography>;

        return (
            <Box sx={{ minWidth: 0, lineHeight: 1.3, py: 0.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: colour.body }}>
                    {formatMoney(amounts.dueNow)}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: colour.faint, whiteSpace: 'normal' }}>
                    {amounts.debt > 0
                        ? `incl. ${formatMoney(amounts.debt)} owed from before`
                        : `then ${formatMoney(amounts.overTheYear)} over the year`}
                </Typography>
            </Box>
        );
    },
});
